import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import PageTabBar from '../components/PageTabBar';
import StockSatelliteHeader from '../features/stock/StockSatelliteHeader';
import {
  STOCK_PANEL, STOCK_BTN_PRIMARY, STOCK_BTN_SECONDARY, STOCK_FILTER_ACTIVE, STOCK_FILTER_INACTIVE, STOCK_KPI_CARD,
} from '../features/stock/stockV2Ui';
import { STOCK_TABS } from './_stockTabs';
import { useAuth } from '../contexts/AuthContext';
import { Search, Link2, X, CheckCircle, AlertTriangle, Clock, Eye, ChevronLeft, ChevronRight, RefreshCw, FileText, Package } from 'lucide-react';

const STATUS_CONFIG = {
  non_traite: { label: 'Non traité', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertTriangle },
  en_attente: { label: 'En attente', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  regularise: { label: 'Régularisé', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  ignore: { label: 'Ignoré', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: X },
};

const REASON_LABELS = {
  reference_generique: 'Référence générique',
  produit_inexistant: 'Produit inexistant en stock',
  stock_zero: 'Stock à zéro',
};

const ReconciliationPage = () => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [linkModal, setLinkModal] = useState({ show: false, item: null });
  const [stockSearch, setStockSearch] = useState('');
  const [stockSuggestions, setStockSuggestions] = useState([]);
  const [searchingStock, setSearchingStock] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [linkQuantite, setLinkQuantite] = useState(1);
  const [linkMotif, setLinkMotif] = useState('');
  const [linking, setLinking] = useState(false);
  const [ignoreModal, setIgnoreModal] = useState({ show: false, item: null });
  const [ignoreMotif, setIgnoreMotif] = useState('');
  const [ignoring, setIgnoring] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: 30 });
      if (statusFilter) params.append('status', statusFilter);
      const res = await api.get(`/reconciliation/unmatched-sales?${params}`);
      setItems(res.data.items || []);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      console.error('Erreur chargement réconciliation:', err);
    } finally {
      setLoading(false);
    }
  }, [api, currentPage, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/reconciliation/stats');
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [api]);

  useEffect(() => { fetchItems(); fetchStats(); }, [fetchItems, fetchStats]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await api.post('/reconciliation/scan');
      const msg = `Scan terminé: ${res.data.scanned_ventes} ventes analysées, ${res.data.new_items_found} nouveaux éléments détectés`;
      alert(msg);
      fetchItems();
      fetchStats();
    } catch (err) {
      alert('Erreur lors du scan: ' + (err.response?.data?.detail || err.message));
    } finally {
      setScanning(false);
    }
  };

  const handleSearchStock = async (q) => {
    setStockSearch(q);
    if (q.length < 2) { setStockSuggestions([]); return; }
    setSearchingStock(true);
    try {
      const res = await api.get(`/reconciliation/stock-suggestions?query=${encodeURIComponent(q)}`);
      setStockSuggestions(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setSearchingStock(false);
    }
  };

  const handleLink = async () => {
    if (!selectedStock || !linkModal.item) return;
    setLinking(true);
    try {
      await api.post('/reconciliation/link', {
        recon_id: linkModal.item.id,
        stock_ref: selectedStock.reference,
        quantite: linkQuantite,
        motif: linkMotif
      });
      setLinkModal({ show: false, item: null });
      setSelectedStock(null);
      setStockSearch('');
      setStockSuggestions([]);
      setLinkQuantite(1);
      setLinkMotif('');
      fetchItems();
      fetchStats();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLinking(false);
    }
  };

  const handleIgnore = async () => {
    if (!ignoreModal.item) return;
    setIgnoring(true);
    try {
      await api.post('/reconciliation/ignore', {
        recon_id: ignoreModal.item.id,
        motif: ignoreMotif || 'Non applicable'
      });
      setIgnoreModal({ show: false, item: null });
      setIgnoreMotif('');
      fetchItems();
      fetchStats();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIgnoring(false);
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await api.get('/reconciliation/log?limit=100');
      setLogs(res.data.logs || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLogsLoading(false);
    }
  };

  const openLinkModal = (item) => {
    setLinkModal({ show: true, item });
    setSelectedStock(null);
    setStockSearch(item.item_designation || '');
    setLinkQuantite(item.item_quantite || 1);
    setLinkMotif('');
    handleSearchStock(item.item_designation || '');
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-4 fade-in" data-testid="reconciliation-page">
        <PageTabBar tabs={STOCK_TABS} activeColor="rose" />
        <StockSatelliteHeader
          title="Réconciliation POS / Stock"
          subtitle="Ventes hors stock · liaison inventaire"
          backTo="/stock"
          backTestId="reconciliation-back"
        />
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={handleScan}
            disabled={scanning}
            className={STOCK_BTN_PRIMARY}
            data-testid="reconciliation-scan-btn"
          >
            <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scan en cours…' : 'Scanner les ventes'}
          </button>
          <button
            type="button"
            onClick={() => { setShowLog(!showLog); if (!showLog) fetchLogs(); }}
            className={STOCK_BTN_SECONDARY}
            data-testid="reconciliation-log-btn"
          >
            <FileText className="w-4 h-4" />
            {showLog ? 'Masquer le journal' : 'Journal'}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="reconciliation-stats">
          {[
            { key: 'non_traite', label: 'Non traités', color: 'text-red-400' },
            { key: 'en_attente', label: 'En attente', color: 'text-amber-400' },
            { key: 'regularise', label: 'Régularisés', color: 'text-emerald-400' },
            { key: 'ignore', label: 'Ignorés', color: 'text-gray-400' },
            { key: 'total', label: 'Total', color: 'text-white' },
          ].map(s => (
            <div key={s.key} className={STOCK_KPI_CARD}>
              <div className="text-xs text-gray-500">{s.label}</div>
              <div className={`text-xl font-bold ${s.color}`}>{stats[s.key] || 0}</div>
            </div>
          ))}
        </div>

        <div className={`${STOCK_PANEL} p-3 flex flex-wrap gap-2 items-center`}>
          <span className="text-xs text-gray-500 uppercase font-bold">Statut</span>
          {['', 'non_traite', 'en_attente', 'regularise', 'ignore'].map(s => (
            <button
              key={s}
              type="button"
              onClick={() => { setStatusFilter(s); setCurrentPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs ${statusFilter === s ? STOCK_FILTER_ACTIVE : STOCK_FILTER_INACTIVE}`}
              data-testid={`reconciliation-filter-${s || 'all'}`}
            >
              {s ? STATUS_CONFIG[s]?.label : 'Tous'}
            </button>
          ))}
        </div>

        <div className={`${STOCK_PANEL} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full table-dark-custom text-sm" data-testid="reconciliation-table">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Date</th>
                  <th>Article vendu</th>
                  <th>Qté</th>
                  <th>Montant</th>
                  <th>Raison</th>
                  <th>Stock lié</th>
                  <th>Statut</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="9" className="text-center py-8"><div className="loading-spinner mx-auto"></div></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan="9" className="text-center py-8 text-gray-400">
                    {statusFilter ? 'Aucun élément avec ce statut' : 'Aucun élément à réconcilier. Lancez un scan.'}
                  </td></tr>
                ) : items.map(item => {
                  const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.non_traite;
                  const StatusIcon = cfg.icon;
                  return (
                    <tr key={item.id} data-testid={`recon-row-${item.id}`}>
                      <td className="font-mono text-xs text-gray-400">{item.ref_vente}</td>
                      <td className="text-gray-400 text-xs">
                        {item.date_vente ? new Date(item.date_vente).toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td className="font-semibold max-w-[200px] truncate" title={item.item_designation}>
                        {item.item_designation}
                      </td>
                      <td className="text-center">{item.item_quantite}</td>
                      <td className="text-green-400 font-bold">{item.item_prix_total?.toFixed(2)} €</td>
                      <td>
                        <span className="text-xs px-2 py-0.5 rounded bg-white/5 text-gray-300">
                          {REASON_LABELS[item.reason] || item.reason}
                        </span>
                      </td>
                      <td className="text-xs">
                        {item.linked_stock_designation ? (
                          <span className="text-emerald-400">{item.linked_stock_designation}</span>
                        ) : '-'}
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-semibold ${cfg.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {cfg.label}
                        </span>
                      </td>
                      <td>
                        {(item.status === 'non_traite' || item.status === 'en_attente') && (
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => openLinkModal(item)}
                              className={`px-2 py-1 rounded-lg text-xs font-semibold border border-red-500/30 bg-red-950/30 text-red-300 hover:bg-red-950/50 flex items-center gap-1`}
                              data-testid={`recon-link-${item.id}`}
                            >
                              <Link2 className="w-3 h-3" /> Associer
                            </button>
                            <button
                              type="button"
                              onClick={() => { setIgnoreModal({ show: true, item }); setIgnoreMotif(''); }}
                              className={`px-2 py-1 rounded-lg text-xs font-semibold border border-white/10 bg-black/20 text-gray-400 hover:text-white`}
                              data-testid={`recon-ignore-${item.id}`}
                            >
                              Ignorer
                            </button>
                          </div>
                        )}
                        {item.status === 'regularise' && (
                          <span className="text-xs text-emerald-400">Par {item.admin_username}</span>
                        )}
                        {item.status === 'ignore' && (
                          <span className="text-xs text-gray-500">{item.motif}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 py-4">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"><ChevronLeft className="w-5 h-5" /></button>
            <span className="text-sm">Page {currentPage} / {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"><ChevronRight className="w-5 h-5" /></button>
          </div>
        )}

        {/* Log section */}
        {showLog && (
          <div className={`${STOCK_PANEL} p-4 mt-4`} data-testid="reconciliation-log-section">
            <h3 className="text-sm font-bold text-gray-300 mb-4 flex items-center gap-2 uppercase tracking-wide">
              <FileText className="w-4 h-4 text-red-400" /> Journal de réconciliation
            </h3>
            {logsLoading ? (
              <div className="text-center py-6"><div className="loading-spinner mx-auto"></div></div>
            ) : logs.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-4">Aucune action enregistrée</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full table-dark-custom text-xs">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Action</th>
                      <th>Ticket</th>
                      <th>Article vendu</th>
                      <th>Stock associé</th>
                      <th>Ancien stock</th>
                      <th>Nouveau stock</th>
                      <th>Admin</th>
                      <th>Motif</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id}>
                        <td>{log.created_at ? new Date(log.created_at).toLocaleString('fr-FR') : '-'}</td>
                        <td>
                          <span className={log.action === 'link' ? 'text-emerald-400 font-semibold' : 'text-gray-400'}>
                            {log.action === 'link' ? 'Liaison' : 'Ignoré'}
                          </span>
                        </td>
                        <td className="font-mono text-gray-400">{log.ref_vente}</td>
                        <td>{log.item_designation}</td>
                        <td className="text-emerald-400">{log.stock_designation || '-'}</td>
                        <td>{log.ancien_stock ?? '-'}</td>
                        <td>{log.nouveau_stock ?? '-'}</td>
                        <td>{log.admin_username}</td>
                        <td className="max-w-[150px] truncate">{log.motif || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Link Modal */}
      {linkModal.show && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 9999}}>
          <div className="modal-dark w-full max-w-lg" data-testid="reconciliation-link-modal">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2 text-white">
                <Link2 className="w-5 h-5 text-red-400" /> Associer à un produit du stock
              </h3>
              <button onClick={() => setLinkModal({ show: false, item: null })} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Info about the sale item */}
              <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                <div className="text-xs text-gray-400 mb-1">Article vendu</div>
                <div className="font-semibold text-white">{linkModal.item?.item_designation}</div>
                <div className="text-xs text-gray-400 mt-1">
                  Ticket: <span className="text-red-300 font-mono">{linkModal.item?.ref_vente}</span>
                  {' '} | Qté: {linkModal.item?.item_quantite} | {linkModal.item?.item_prix_total?.toFixed(2)} €
                </div>
              </div>

              {/* Search stock */}
              <div>
                <label className="block text-sm text-gray-300 mb-2">Rechercher dans le stock</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={stockSearch}
                    onChange={(e) => handleSearchStock(e.target.value)}
                    className="input-dark w-full py-2.5 pl-9 text-sm"
                    placeholder="Nom ou référence du produit..."
                    data-testid="recon-stock-search"
                  />
                </div>
              </div>

              {/* Suggestions */}
              {stockSuggestions.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-1 border border-white/10 rounded-lg p-2">
                  {stockSuggestions.map(s => (
                    <button
                      key={s.reference}
                      onClick={() => { setSelectedStock(s); setStockSuggestions([]); }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedStock?.reference === s.reference
                          ? 'bg-red-950/40 border border-red-500/40'
                          : 'bg-white/5 hover:bg-white/10 border border-transparent'
                      }`}
                      data-testid={`recon-suggestion-${s.reference}`}
                    >
                      <div className="font-semibold text-white">{s.designation}</div>
                      <div className="text-xs text-gray-400 flex gap-3">
                        <span>Réf: {s.reference}</span>
                        <span className={`font-bold ${s.quantite > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          Stock: {s.quantite}
                        </span>
                        {s.prix_vente && <span>Prix: {Number(s.prix_vente).toFixed(2)} €</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected stock display */}
              {selectedStock && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                  <div className="text-xs text-emerald-400 mb-1">Produit sélectionné</div>
                  <div className="font-semibold text-white">{selectedStock.designation}</div>
                  <div className="text-xs text-gray-400">
                    Réf: {selectedStock.reference} | Stock actuel: <span className="font-bold text-emerald-400">{selectedStock.quantite}</span>
                  </div>
                </div>
              )}

              {/* Quantité */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Quantité à déduire</label>
                <input
                  type="number"
                  value={linkQuantite}
                  onChange={(e) => setLinkQuantite(Math.max(1, parseInt(e.target.value) || 1))}
                  className="input-dark w-full py-2 text-sm"
                  min="1"
                  data-testid="recon-link-quantite"
                />
              </div>

              {/* Motif */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Motif (optionnel)</label>
                <input
                  type="text"
                  value={linkMotif}
                  onChange={(e) => setLinkMotif(e.target.value)}
                  className="input-dark w-full py-2 text-sm"
                  placeholder="Ex: Produit équivalent utilisé"
                  data-testid="recon-link-motif"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setLinkModal({ show: false, item: null })} className={`${STOCK_BTN_SECONDARY} flex-1 py-2.5`}>
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleLink}
                  disabled={!selectedStock || linking}
                  className={`${STOCK_BTN_PRIMARY} flex-1 py-2.5 disabled:opacity-50`}
                  data-testid="recon-link-confirm"
                >
                  {linking ? <div className="loading-spinner w-4 h-4"></div> : <><CheckCircle className="w-4 h-4" /> Valider la liaison</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ignore Modal */}
      {ignoreModal.show && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 9999}}>
          <div className="modal-dark w-full max-w-md" data-testid="reconciliation-ignore-modal">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold text-lg">Ignorer cet élément</h3>
              <button onClick={() => setIgnoreModal({ show: false, item: null })} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-white/5 rounded-lg p-3">
                <div className="font-semibold">{ignoreModal.item?.item_designation}</div>
                <div className="text-xs text-gray-400">Ticket: {ignoreModal.item?.ref_vente}</div>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Motif</label>
                <input
                  type="text"
                  value={ignoreMotif}
                  onChange={(e) => setIgnoreMotif(e.target.value)}
                  className="input-dark w-full py-2 text-sm"
                  placeholder="Ex: Vente sans stock nécessaire"
                  data-testid="recon-ignore-motif"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setIgnoreModal({ show: false, item: null })} className={`${STOCK_BTN_SECONDARY} flex-1 py-2.5`}>Annuler</button>
                <button type="button" onClick={handleIgnore} disabled={ignoring} className={`${STOCK_BTN_PRIMARY} flex-1 py-2.5 disabled:opacity-50`} data-testid="recon-ignore-confirm">
                  {ignoring ? 'En cours...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ReconciliationPage;
